/* eslint-disable prettier/prettier */
import React, { useEffect, useRef, useState } from 'react';
import styles from '../../../styles/components/pageComponents/program/AdminProgramCard.module.css';
import { ReactComponent as IcfLogo } from '../../../assets/icons/IcfLogo.svg';
import { useUpdateCourse } from '../../../hooks/useUpdateCourse';
import SuccessContent from '../../../components/controlVariations/ModularSuccessModal';
import ResponsiveModal from '../../../components/controlVariations/ResponsiveModal';
import ErrorPopupModal from '../../../components/controlVariations/ErrorPopupMoadal';
import {
    DuplicateProgramModal,
    DuplicateFormData,
} from '../../../components/pageComponents/program/content/DuplicateProgramModal';
import { useDuplicateCourse } from '../../../hooks/useCourse';
import { ProgramData } from '../../../types/program';

const formatCertification = (cert?: string, maxLength = 16) => {
    if (!cert?.trim()) return '--';

    const value = cert.trim();

    // If already short enough, don't abbreviate
    if (value.length <= maxLength) {
        return value;
    }

    const ignoreWords = new Set([
        'and',
        'of',
        'the',
        'for',
        'to',
        'in',
        'on',
        'at',
        'with',
        '&',
    ]);

    const abbreviation = value
        .split(/\s+/)
        .filter(word => !ignoreWords.has(word.toLowerCase()))
        .map(word => word[0].toUpperCase())
        .join('');

    return abbreviation || value;
};

interface AdminProgramCardProps {
    program: ProgramData;
    onViewClick: (id: string | number) => void;
    customClass?: string;
    programStatus: 'published' | 'draft' | 'suspended' | 'archived';
    onStatusChange: (id: string, status: string) => void;
    onDuplicate: (course: any) => void;
    onEnrollLearners?: () => void;
}

const AdminProgramCard: React.FC<AdminProgramCardProps> = ({
    program,
    onViewClick,
    customClass = '',
    programStatus,
    onStatusChange,
    onDuplicate,
    onEnrollLearners
}) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);


    const getDuration = (start?: string | Date, end?: string | Date) => {
        if (!start || !end) return '--';

        const startDate = new Date(start);
        const endDate = new Date(end);

        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return '--';

        const diffTime = endDate.getTime() - startDate.getTime();

        if (diffTime <= 0) return '--';

        const totalDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        const months = Math.floor(totalDays / 30);
        const days = totalDays % 30;

        if (months > 0 && days > 0) return `${months}Month ${days}Days`;
        if (months > 0) return `${months}Months`;
        return `${days}Days`;
    };

    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const { duplicateCourse } = useDuplicateCourse();

    const isDraft = programStatus.toLowerCase() === 'draft';
    const isSuspended = programStatus.toLowerCase() === 'suspended';
    const isArchived = programStatus.toLowerCase() === 'archived';
    const isPublished = programStatus.toLowerCase() === 'published';

    const [successModal, setSuccessModal] = useState<{
        open: boolean;
        title: string;
        subtitle: string;
    }>({
        open: false,
        title: '',
        subtitle: '',
    });

    const buildDuplicatePayload = (data: DuplicateFormData) => {
        return {
            title: data.title,
            solutionid: data.solution,
            startdate: data.startDate,
            enddate: data.endDate,
            copyResources: data.copyResources,
            copyAssessments: data.copyAssessments,
        };
    };

    const showError = (msg: string) => {
        setErrorMessage(msg);
        setShowErrorModal(true);
    };

    const showSuccess = (title: string, subtitle: string) => {
        setSuccessModal({
            open: true,
            title,
            subtitle,
        });

        setTimeout(() => {
            setSuccessModal(prev => ({ ...prev, open: false }));
        }, 2500);
    };

    const { updateCourse, loading } = useUpdateCourse();

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(event.target as Node)
            ) {
                setIsMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleMarkComplete = async () => {
        try {
            const response = await updateCourse(String(program.id), {
                status: 'suspended',
            });

            if (!response) {
                showError('Failed to mark program as complete');
                return;
            }

            onStatusChange(String(program.id), 'suspended');

            showSuccess(
                'Program Completed',
                'The program has been marked as complete successfully.'
            );
        } catch (err: any) {
            showError(err?.message || 'Something went wrong');
        } finally {
            setIsMenuOpen(false);
        }
    };

    const handleDuplicate = () => {
        setShowDuplicateModal(true);
        setIsMenuOpen(false);
    };

    const handleDuplicateSubmit = async (data: DuplicateFormData) => {
        try {
            const payload = buildDuplicatePayload(data);

            const newCourse = await duplicateCourse(
                String(program.id),
                payload
            );

            onDuplicate(newCourse);
            setShowDuplicateModal(false);

            showSuccess(
                'Program Duplicated',
                'A copy of the program has been created successfully.'
            );
        } catch (err: any) {
            showError(err?.message || 'Failed to duplicate program');
        }
    };

    const handleArchive = async () => {
        try {
            const response = await updateCourse(String(program.id), {
                status: 'archived',
            });

            if (!response) {
                showError('Failed to archive program');
                return;
            }

            onStatusChange(String(program.id), 'archived');

            showSuccess(
                'Program Archived',
                'The program has been archived successfully.'
            );
        } catch (err: any) {
            showError(err?.message || 'Something went wrong');
        } finally {
            setIsMenuOpen(false);
        }
    };

    const handleRepublish = async () => {
        try {
            const response = await updateCourse(String(program.id), {
                status: 'published',
				ispublic: true
            });

            if (!response) {
                showError('Failed to republish program');
                return;
            }

            onStatusChange(String(program.id), 'published');

            if (isDraft) {
                showSuccess(
                    'Program Published Successfully',
                    'The program is now live.'
                );
            } else {
                showSuccess(
                    'Program Republished Successfully',
                    'The program is now live again.'
                );
            }
        } catch (err: any) {
            showError(err?.message || 'Something went wrong');
        } finally {
            setIsMenuOpen(false);
        }
    };

    return (
        <>
            <div
                className={`${styles.cardContainer} ${customClass} ${isDraft ? styles.draftCard : ''} ${isSuspended ? styles.suspendedCard : ''} ${isArchived ? styles.archivedCard : ''}`}
            >
                {/* Header Image Section */}
                <div className={styles.imageWrapper}>
                    <img
                        src={program.imageUrl}
                        alt={program.title}
                        className={styles.cardImage}
                    />

                    <div className={styles.icfLogo}>
                        <IcfLogo />
                    </div>

                    {programStatus === 'draft' && (
                        <span className={styles.draftBadge}>Draft</span>
                    )}

                    {programStatus === 'suspended' && (
                        <span className={styles.completedBadge}>Completed</span>
                    )}
                </div>

                {/* Content Section */}
                <div className={styles.contentBody}>
                    <div className={styles.categoryRow}>
                        <div className={styles.iconBox}>
                            <svg
                                width="22"
                                height="22"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#802868"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                                <path d="M6 12v5c3 3 9 3 12 0v-5" />
                            </svg>
                        </div>

                        <span className={styles.categoryText}>
                            {program.solutiontitle}
                        </span>

                        {/* Three Dots Menu */}
                        <div className={styles.menuContainer} ref={menuRef}>
                            <button
                                className={styles.moreOptionsButton}
                                onClick={() => setIsMenuOpen(!isMenuOpen)}
                                aria-label="More options"
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="#828282"
                                >
                                    <circle cx="5" cy="12" r="2" />
                                    <circle cx="12" cy="12" r="2" />
                                    <circle cx="19" cy="12" r="2" />
                                </svg>
                            </button>

                            {isMenuOpen && (
                                <div className={styles.dropdownMenu}>
                                    {isDraft || isArchived ? (
                                        <>
                                            <button
                                                className={styles.dropdownItem}
                                                onClick={handleRepublish}
                                                disabled={loading}
                                            >
                                                {loading ? 'Updating...' : 'Publish Program'}
                                            </button>

                                            <button
                                                className={styles.dropdownItem}
                                                onClick={handleDuplicate}
                                            >
                                                Duplicate Program
                                            </button>
                                        </>
                                    ) : isSuspended ? (
                                        <>
                                            <button
                                                className={styles.dropdownItem}
                                                onClick={handleArchive}
                                                disabled={loading}
                                            >
                                                {loading ? 'Updating...' : 'Archive Program'}
                                            </button>

                                            <button
                                                className={styles.dropdownItem}
                                                onClick={handleRepublish}
                                                disabled={loading}
                                            >
                                                {loading ? 'Updating...' : 'Republish Program'}
                                            </button>
                                        </>
                                    ) : isPublished ? (
                                        <>
                                            <button
                                                className={styles.dropdownItem}
                                                onClick={handleMarkComplete}
                                                disabled={loading}
                                            >
                                                {loading ? 'Updating...' : 'Mark as complete'}
                                            </button>

                                            <button
                                                className={styles.dropdownItem}
                                                onClick={handleDuplicate}
                                            >
                                                Duplicate Program
                                            </button>

                                            <button
                                                className={styles.dropdownItem}
                                                onClick={() => {
                                                    setIsMenuOpen(false);
                                                    onEnrollLearners?.();
                                                }}
                                            >
                                                Enroll Learners
                                            </button>
                                        </>
                                    ) : (
                                        <div className={styles.dropdownItem}>No Status Assigned</div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <h3 className={styles.programTitle}>{program.title}</h3>

                    <div className={styles.metaGrid}>
                        <div className={styles.metaItem}>
                            {program.programformat}
                        </div>
                        <div
                            className={styles.metaItem}
                            title={program.certification || '--'}
                        >
                            {formatCertification(program.certification)}
                        </div>
                        
                        <div className={styles.metaItem}>
                            {getDuration(program.startdate, program.enddate)}
                        </div>
                    </div>

                    <div className={styles.buttonGroup}>
                        <button
                            onClick={() => onViewClick(program.id)}
                            className={styles.viewButton}
                        >
                            {programStatus === 'draft'
                                ? 'Edit Draft'
                                : 'View Program'}
                        </button>
                    </div>
                </div>
            </div>

            {/* SUCCESS MODAL */}
            <ResponsiveModal
                open={successModal.open}
                onClose={() =>
                    setSuccessModal(prev => ({ ...prev, open: false }))
                }
            >
                <SuccessContent
                    title={successModal.title}
                    subtitle={successModal.subtitle}
                />
            </ResponsiveModal>

            {/* ERROR MODAL */}
            <ResponsiveModal
                open={showErrorModal}
                onClose={() => setShowErrorModal(false)}
            >
                <ErrorPopupModal
                    title={errorMessage}
                    onCancel={() => setShowErrorModal(false)}
                />
            </ResponsiveModal>

            {/* DUPLICATE PROGRAM MODAL */}
            <DuplicateProgramModal
                isOpen={showDuplicateModal}
                onClose={() => setShowDuplicateModal(false)}
                onSubmit={handleDuplicateSubmit}
                initialValues={{
                    solution: program.category,
                    title: `${program.title} (Copy)`,
                    startDate: '',
                    endDate: '',
                }}
            />
        </>
    );
};

export default AdminProgramCard;